import React, { useRef, useEffect, useState, useCallback } from 'react'
import { cn } from '@/utils'

export interface QlikScriptEditorProps {
  /**
   * Initial script content
   */
  initialScript?: string
  
  /**
   * Callback when script content changes
   */
  onChange?: (value: string) => void
  
  /**
   * Available Qlik variables for autocomplete
   */
  variables?: string[]
  
  /**
   * Editor theme
   */
  theme?: 'light' | 'dark'
  
  /**
   * Editor height
   */
  height?: string
  
  /**
   * Editor width
   */
  width?: string
  
  /**
   * Whether the editor is read-only
   */
  readOnly?: boolean
  
  /**
   * Additional CSS class
   */
  className?: string
  
  /**
   * Placeholder text when editor is empty
   */
  placeholder?: string
}

/**
 * A Qlik Script Editor component with syntax highlighting and autocomplete
 */
export const QlikScriptEditor: React.FC<QlikScriptEditorProps> = ({
  initialScript = '',
  onChange,
  variables = [],
  theme = 'light',
  height = '400px',
  width = '100%',
  readOnly = false,
  className,
  placeholder = 'Enter your Qlik script here...'
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [script, setScript] = useState(initialScript)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [cursorPosition, setCursorPosition] = useState(0)

  // Qlik keywords for syntax highlighting
  const qlikKeywords = [
    'LOAD', 'FROM', 'SELECT', 'WHERE', 'GROUP BY', 'ORDER BY', 'LEFT JOIN', 'RIGHT JOIN', 'INNER JOIN',
    'OUTER JOIN', 'CONCATENATE', 'MAPPING', 'QUALIFY', 'UNQUALIFY', 'DROP', 'STORE', 'LET', 'SET',
    'CALL', 'SUB', 'END SUB', 'FOR', 'NEXT', 'DO', 'LOOP', 'WHILE', 'WEND', 'IF', 'THEN', 'ELSE',
    'ELSEIF', 'END IF', 'SWITCH', 'CASE', 'DEFAULT', 'END SWITCH', 'EXIT SCRIPT', 'TRACE'
  ]

  // Qlik functions for autocomplete
  const qlikFunctions = [
    'Sum', 'Count', 'Avg', 'Min', 'Max', 'Date', 'Today', 'Now', 'Year', 'Month', 'Day',
    'Hour', 'Minute', 'Second', 'If', 'Alt', 'Match', 'ApplyMap', 'Lookup', 'Peek', 'Previous',
    'Exists', 'FieldValue', 'FieldIndex', 'NoOfRows', 'RowNo', 'RecNo', 'Len', 'Left', 'Right',
    'Mid', 'Trim', 'Upper', 'Lower', 'Replace', 'SubField', 'PurgeChar', 'KeepChar', 'Chr', 'Ord'
  ]

  const handleScriptChange = useCallback((value: string) => {
    setScript(value)
    onChange?.(value)
  }, [onChange])

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    const position = e.target.selectionStart || 0
    
    handleScriptChange(value)
    setCursorPosition(position)
    
    // Simple autocomplete logic
    const currentWord = getCurrentWord(value, position)
    if (currentWord.length > 0) {
      const matchingSuggestions = [
        ...qlikKeywords.filter(keyword => 
          keyword.toLowerCase().startsWith(currentWord.toLowerCase())
        ),
        ...qlikFunctions.filter(func => 
          func.toLowerCase().startsWith(currentWord.toLowerCase())
        ),
        ...variables.filter(variable => 
          variable.toLowerCase().startsWith(currentWord.toLowerCase())
        )
      ].slice(0, 10)
      
      setSuggestions(matchingSuggestions)
      setShowSuggestions(matchingSuggestions.length > 0)
    } else {
      setShowSuggestions(false)
    }
  }

  const getCurrentWord = (text: string, position: number): string => {
    const beforeCursor = text.slice(0, position)
    const afterCursor = text.slice(position)
    
    const wordStart = beforeCursor.search(/\w+$/)
    const wordEnd = afterCursor.search(/\W/)
    
    if (wordStart === -1) return ''
    
    const start = wordStart
    const end = position + (wordEnd === -1 ? afterCursor.length : wordEnd)
    
    return text.slice(start, end)
  }

  const applySuggestion = (suggestion: string) => {
    if (!textareaRef.current) return
    
    const textarea = textareaRef.current
    const currentWord = getCurrentWord(script, cursorPosition)
    const wordStart = script.slice(0, cursorPosition).lastIndexOf(currentWord)
    
    if (wordStart !== -1) {
      const newScript = 
        script.slice(0, wordStart) + 
        suggestion + 
        script.slice(wordStart + currentWord.length)
      
      handleScriptChange(newScript)
      setShowSuggestions(false)
      
      // Focus back to textarea
      setTimeout(() => {
        textarea.focus()
        const newPosition = wordStart + suggestion.length
        textarea.setSelectionRange(newPosition, newPosition)
      }, 0)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle Tab for indentation
    if (e.key === 'Tab') {
      e.preventDefault()
      const textarea = e.currentTarget
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const value = textarea.value

      // Insert tab character
      const newValue = value.substring(0, start) + '  ' + value.substring(end)
      handleScriptChange(newValue)

      // Move cursor
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2
      }, 0)
    }
    
    // Close suggestions on Escape
    if (e.key === 'Escape') {
      setShowSuggestions(false)
    }
  }

  useEffect(() => {
    if (initialScript !== script) {
      setScript(initialScript)
    }
  }, [initialScript, script])

  return (
    <div className={cn('qlik-script-editor', className)} style={{ width }}>
      <div 
        className={cn(
          'qlik-script-editor__container',
          `qlik-script-editor--${theme}`,
          {
            'qlik-script-editor--readonly': readOnly
          }
        )}
        style={{ height }}
      >
        <textarea
          ref={textareaRef}
          value={script}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          readOnly={readOnly}
          className="qlik-script-editor__textarea"
          spellCheck={false}
        />
        
        {showSuggestions && suggestions.length > 0 && (
          <div className="qlik-script-editor__suggestions">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                className="qlik-script-editor__suggestion"
                onClick={() => applySuggestion(suggestion)}
                type="button"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}